import React, { useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useAuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import paymentApi from '../../../api/payments/paymentApi';
import './PaymentModal.css';

// Import payment method logos (using the existing paths from SmsTopup)
import mpesaLogo from '../../../AdminPanel/Payment-History/images/2.png';
import tigopesaLogo from '../../../AdminPanel/Payment-History/images/1.png';
import airtelLogo from '../../../AdminPanel/Payment-History/images/3.png';

const PaymentModal = ({
  show,
  handleClose,
  amount,
  title = "Complete Payment",
  summaryChildren,
  paymentId,
  onPaymentSuccess,
  onPaymentError,
  mnoOptions = []
}) => {
  const { isAuthenticated } = useAuthContext();
  const [selectedPayment, setSelectedPayment] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const paymentMethods = mnoOptions && mnoOptions.length > 0
    ? mnoOptions.map(opt => ({
      id: opt.paymentname,
      name: opt.displayname,
      image: opt.logo || (opt.displayname.toLowerCase().includes('tigo') ? tigopesaLogo : opt.displayname.toLowerCase().includes('pesa') ? mpesaLogo : airtelLogo)
    }))
    : [

    ];

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!selectedPayment) {
      toast.warning('Tafadhali chagua njia ya malipo');
      return;
    }
    if (!phoneNumber) {
      toast.warning('Tafadhali ingiza namba ya simu');
      return;
    }

    setLocalLoading(true);
    setStatusMessage('Initiating transaction...');
    try {
      // 1. Map selected payment method to MNO format
      let mno = selectedPayment;


      const chargeData = {
        payment_id: paymentId,
        mno: mno,
        phone_number: phoneNumber
      };

      // 2. Charge the mobile number
      await paymentApi.chargePayment(chargeData);

      toast.info("Payment initiated! Please enter your PIN on your phone to complete the transaction.");
      setStatusMessage('Waiting for PIN confirmation on your phone...');

      // 3. Poll payment status verification
      let attempts = 0;
      const maxAttempts = 4; // 120 seconds total (4 attempts * 30s interval = 120 seconds / 2 minutes)

      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await paymentApi.checkPaymentStatus(paymentId);
          const data = statusRes?.data?.data || statusRes?.data || statusRes || {};

          // status === 1 means success
          if (data.status === 1 || data.payment?.status === 1) {
            clearInterval(pollInterval);
            setLocalLoading(false);
            setStatusMessage('');
            setSelectedPayment('');
            setPhoneNumber('');
            if (onPaymentSuccess) {
              onPaymentSuccess(data);
            }
          } else if (data.status === 2 || data.payment?.status === 2) {
            clearInterval(pollInterval);
            setLocalLoading(false);
            setStatusMessage('');
            const failedErr = new Error("Payment transaction failed on the gateway.");
            if (onPaymentError) onPaymentError(failedErr);
          }
        } catch (pollErr) {
          console.error("Status polling error:", pollErr);
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setLocalLoading(false);
          setStatusMessage('');
          const timeoutErr = new Error("Malipo yanasubiri kukamilika. Tafadhali thibitisha kwenye historia ya malipo baada ya kuingiza PIN.");
          if (onPaymentError) onPaymentError(timeoutErr);
        }
      }, 30000);

    } catch (err) {
      console.error('Error charging payment:', err);
      setLocalLoading(false);
      setStatusMessage('');
      if (onPaymentError) onPaymentError(err);
    }
  };

  const handleModalClose = () => {
    if (localLoading) {
      toast.warning('Tafadhali subiri malipo yakamilike');
      return;
    }
    setSelectedPayment('');
    setPhoneNumber('');
    setStatusMessage('');
    handleClose();
  };

  if (!isAuthenticated) return null;

  return (
    <Modal show={show} onHide={handleModalClose} centered size="md" backdrop="static">
      <Modal.Body className="p-0">
        <div className="payment-container">
          <h2 className="stylish-heading-payment text-center mt-3">{title}</h2>

          <div className="payment-summary shadow-sm">
            <h5 className="fw-bold mb-3 text-secondary">Order Summary</h5>

            <div className="payment-summary-details">
              {summaryChildren}
            </div>

            <div className="payment-summary-total">
              <span>Total Amount Due</span>
              <strong className="text-primary">TZS {amount.toLocaleString()}</strong>
            </div>
          </div>

          <h5 className="fw-bold mb-3 mt-4 text-center text-secondary">Select Payment Method</h5>
          <div className="payment-methods">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className={`payment-method ${selectedPayment === method.id ? 'selected' : ''} ${localLoading ? 'disabled' : ''}`}
                onClick={() => !localLoading && setSelectedPayment(method.id)}
              >
                <img
                  src={method.image}
                  alt={method.name}
                />
                <div className="payment-method-name">{method.name}</div>
              </div>
            ))}
          </div>

          {selectedPayment && (
            <div className="phone-input-wrapper animate-fade-in">
              <label htmlFor="paymentPhoneNumber">Mobile Money Number</label>
              <input
                id="paymentPhoneNumber"
                type="text"
                inputMode="numeric"
                placeholder="e.g. 0712345678"
                value={phoneNumber}
                disabled={localLoading}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, '');
                  setPhoneNumber(numericValue);
                }}
                pattern="[0-9]*"
              />
              <small className="text-muted mt-1 d-block">Enter the number that will receive the payment prompt.</small>
            </div>
          )}

          {statusMessage && (
            <div className="text-center mt-3 mb-2 text-primary fw-semibold animate-pulse" style={{ fontSize: '0.9rem' }}>
              <Spinner animation="grow" size="sm" className="me-2" />
              {statusMessage}
            </div>
          )}

          <div className="payment-actions">
            <button className="btn-back" onClick={handleModalClose} disabled={localLoading}>
              <i className="bi bi-arrow-left me-2"></i> Close
            </button>
            <button
              className="btn-pay"
              onClick={handleSubmit}
              disabled={localLoading || !selectedPayment || !phoneNumber}
            >
              {localLoading ? (
                <>Processing <Spinner animation="border" size="sm" className="ms-2" /></>
              ) : (
                <>Pay TZS {amount.toLocaleString()} <i className="bi bi-shield-lock-fill ms-1"></i></>
              )}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PaymentModal;
